import * as React from "react";
import { useState, useCallback } from "react";
// Constants
import { excelLogoIconProps, oneNoteLogoIconProps, pdfIconProps, powerPointLogoIconProps, projectLogoIconProps, visioLogoIconProps, wordLogoIconProps, linkIconProps, imageIconProps, formsLogoFilledIconProps } from "../../../icons/icons.styles";
// Controls
import { Icon, Image, ImageFit, ImageLoadState } from "@fluentui/react";
import { SupportedReferenceTypes } from "@ms/uno-models/lib/local/client/taskAttachment/ITaskAttachmentType";
// Styles
import { getDocumentPreviewIconClassNames } from "./DocumentPreviewIcon.styles";
// Utilities
import { css } from "@fluentui/utilities";
import { getFavIconUrl } from "@ms/uno-utilities/lib/local/UrlUtilities";
/**
 * Renders a document preview icon based on the file type of the document.
 * @param props The props for the DocumentPreviewIcon component.
 * @returns A React component that renders a document preview icon.
 */ export function DocumentPreviewIcon(props) {
    const { className, icons, taskAttachmentRowItem, styles } = props;
    /**
     * The current state of the preview image load.
     */ const [previewImageLoadState, setPreviewImageLoadState] = useState(ImageLoadState.notLoaded);
    /**
     * Get the classnames associated with the computed styles
     */ const getClassNames = ()=>{
        const fileType = taskAttachmentRowItem.taskAttachmentRowItemType.taskAttachmentType;
        return getDocumentPreviewIconClassNames(fileType, styles);
    };
    /**
     * Callback method for when the image load state has changed.
     * @param [loadState] The 'loadState' parameter indicates the current state of the Image.
     */ const onPhotoLoadingStateChange = useCallback((loadState)=>{
        setPreviewImageLoadState(loadState);
    }, []);
    /**
     * Get the icon props for the task attachment type
     * @param taskAttachmentRowItem Task Attachment to retrieve the icon props
     */ const getIconPropsForRowItem = (taskAttachmentRowItem)=>{
        let iconPropsByFileType;
        switch(taskAttachmentRowItem.taskAttachmentRowItemType.taskAttachmentType){
            case SupportedReferenceTypes.Excel:
                iconPropsByFileType = icons?.excel ?? excelLogoIconProps;
                break;
            case SupportedReferenceTypes.OneNote:
                iconPropsByFileType = icons?.onenote ?? oneNoteLogoIconProps;
                break;
            case SupportedReferenceTypes.Form:
                iconPropsByFileType = icons?.forms ?? formsLogoFilledIconProps;
                break;
            case SupportedReferenceTypes.Pdf:
                iconPropsByFileType = icons?.pdf ?? pdfIconProps;
                break;
            case SupportedReferenceTypes.PowerPoint:
                iconPropsByFileType = icons?.powerpoint ?? powerPointLogoIconProps;
                break;
            case SupportedReferenceTypes.Project:
                iconPropsByFileType = icons?.project ?? projectLogoIconProps;
                break;
            case SupportedReferenceTypes.Visio:
                iconPropsByFileType = icons?.visio ?? visioLogoIconProps;
                break;
            case SupportedReferenceTypes.Word:
                iconPropsByFileType = icons?.word ?? wordLogoIconProps;
                break;
            default:
                if (!taskAttachmentRowItem.hasKnownExtension()) {
                    iconPropsByFileType = icons?.link ?? linkIconProps;
                } else {
                    iconPropsByFileType = icons?.photo ?? imageIconProps;
                }
        }
        const previewIconClassNames = getClassNames();
        const iconClassName = css(previewIconClassNames.root, className, iconPropsByFileType.className);
        return {
            title: taskAttachmentRowItem.url,
            ...iconPropsByFileType,
            className: iconClassName
        };
    };
    if (!taskAttachmentRowItem.hasKnownExtension() && previewImageLoadState !== ImageLoadState.error) {
        return /*#__PURE__*/ React.createElement("div", {
            className: css(className, "link")
        }, /*#__PURE__*/ React.createElement(Image, {
            shouldFadeIn: true,
            imageFit: ImageFit.none,
            src: getFavIconUrl(taskAttachmentRowItem.url),
            alt: "",
            onLoadingStateChange: onPhotoLoadingStateChange
        }));
    } else if (previewImageLoadState === ImageLoadState.error || !taskAttachmentRowItem.documentPreviewThumbnail) {
        return /*#__PURE__*/ React.createElement(Icon, getIconPropsForRowItem(taskAttachmentRowItem));
    } else {
        return /*#__PURE__*/ React.createElement("div", {
            className: css(className, "thumbnailImage")
        }, /*#__PURE__*/ React.createElement(Image, {
            shouldFadeIn: true,
            imageFit: ImageFit.none,
            src: taskAttachmentRowItem.documentPreviewThumbnail,
            alt: "",
            onLoadingStateChange: onPhotoLoadingStateChange
        }));
    }
}
